/**
 * Browser-side CanvasKit scene renderer.
 *
 * Wraps the shared renderer from @nexmail/shared-util with browser-specific
 * concerns: tiered image caching, optimized image URLs, async loading,
 * and viewport-aware resolution selection.
 *
 * The actual rendering logic lives in the shared package so that the
 * puppet server produces identical output.
 */
import type { CanvasKit, Canvas, Image as SkImage, Paint } from 'canvaskit-wasm';
import type { FontMgr } from 'canvaskit-wasm/full';
import type { SceneDocument, SceneNode } from '@/lib/scene-document';
import { optimizedImageUrl, optimalWidth } from '@/lib/optimized-image-url';
import {
  renderNode as sharedRenderNode,
  type SceneRenderContext,
} from '@nexmail/shared-util';

// Re-export shared helpers used directly by the editor
export { parseColor, applyFillToPaint, buildStarPath, buildPolygonPath } from '@nexmail/shared-util';

// ─── Tiered image cache ──────────────────────────────────────────────────
//
// Tier 1 (GPU):  imageCache   — decoded SkImage textures, expensive (~48MB each)
// Tier 2 (RAM):  blobCache    — compressed source bytes, cheap (~2MB each)
//
// When Tier 1 exceeds MAX_GPU_ENTRIES, SkImages are freed but blobs stay.
// Re-decoding from a blob is ~5ms (no network round-trip, no placeholder flash).

const MAX_GPU_ENTRIES = 80;
const MAX_BLOB_ENTRIES = 200;
const imageCache = new Map<string, SkImage>();
const blobCache = new Map<string, Uint8Array>();
const pendingImages = new Map<string, Promise<SkImage | null>>();
// Negative cache: srcs that failed to load/decode, with the failure time.
// Without this a broken URL is re-fetched on every render frame -- the
// renderer calls onImageMissing each frame the image stays unresolved.
const FAILED_RETRY_MS = 30_000;
const failedImages = new Map<string, number>();

// ─── Network throttle ──────────────────────────────────────────────────────
//
// A large email can reference dozens of distinct images. Firing every fetch at
// once bursts the media CDN past its per-IP rate limit -- it returns 429, the
// loads negative-cache, and the canvas renders blank tiles. Cap the in-flight
// image fetches and retry throttled/transient responses with backoff so big
// documents drain in waves instead of stampeding.
const MAX_CONCURRENT_FETCHES = 6;
let activeFetches = 0;
const fetchWaiters: Array<() => void> = [];

function acquireFetchSlot(): Promise<void> {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (activeFetches < MAX_CONCURRENT_FETCHES) {
        activeFetches++;
        resolve();
      } else {
        fetchWaiters.push(tryAcquire);
      }
    };
    tryAcquire();
  });
}

function releaseFetchSlot() {
  activeFetches--;
  fetchWaiters.shift()?.();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch image bytes through the concurrency gate, retrying 429/5xx with
 * exponential backoff (honoring Retry-After when present). The slot is held
 * across the backoff so a throttled CDN sees fewer concurrent retries, not
 * more. Returns null for non-retryable failures (404/CORS) and once the retry
 * budget is spent -- the caller then tries the <img>-element decoder path.
 */
async function fetchImageBytes(src: string): Promise<Uint8Array | null> {
  await acquireFetchSlot();
  try {
    const MAX_ATTEMPTS = 4;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      let res: Response;
      try {
        res = await fetch(src);
      } catch {
        return null; // network/CORS error -- not retryable here
      }
      if (res.ok) return new Uint8Array(await res.arrayBuffer());
      const throttled = res.status === 429 || res.status >= 500;
      if (!throttled || attempt === MAX_ATTEMPTS - 1) return null;
      const retryAfter = Number(res.headers.get('retry-after'));
      const backoff =
        Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : 300 * 2 ** attempt + Math.random() * 200;
      await sleep(Math.min(backoff, 4000));
    }
    return null;
  } finally {
    releaseFetchSlot();
  }
}

function evictIfNeeded() {
  if (imageCache.size <= MAX_GPU_ENTRIES) return;
  const toEvict = imageCache.size - MAX_GPU_ENTRIES;
  let evicted = 0;
  for (const [key, img] of imageCache) {
    if (evicted >= toEvict) break;
    img.delete();
    imageCache.delete(key);
    evicted++;
  }
}

function evictBlobsIfNeeded() {
  if (blobCache.size <= MAX_BLOB_ENTRIES) return;
  const toEvict = blobCache.size - MAX_BLOB_ENTRIES;
  let evicted = 0;
  for (const key of blobCache.keys()) {
    if (evicted >= toEvict) break;
    if (!imageCache.has(key)) {
      blobCache.delete(key);
      evicted++;
    }
  }
}

function findCachedFallback(rawSrc: string): SkImage | null {
  for (const [key, img] of imageCache) {
    if (key === rawSrc || key.includes(rawSrc)) return img;
  }
  try {
    const pathname = new URL(rawSrc).pathname;
    for (const [key, img] of imageCache) {
      if (key.includes(pathname)) return img;
    }
  } catch {
    // not a full URL
  }
  return null;
}

export function loadImage(ck: CanvasKit, src: string): Promise<SkImage | null> {
  const cached = imageCache.get(src);
  if (cached) return Promise.resolve(cached);

  const pending = pendingImages.get(src);
  if (pending) return pending;

  const failedAt = failedImages.get(src);
  if (failedAt !== undefined) {
    if (Date.now() - failedAt < FAILED_RETRY_MS) return Promise.resolve(null);
    failedImages.delete(src);
  }

  const blob = blobCache.get(src);
  if (blob) {
    const img = ck.MakeImageFromEncoded(blob);
    if (img) {
      imageCache.set(src, img);
      evictIfNeeded();
    }
    return Promise.resolve(img);
  }

  // Fetch through the concurrency gate. Our CloudFront media (dev + prod) is
  // configured with CORS for the web origins (see infra/media/cloudfront.tf),
  // and external URLs are signed/proxied through `/_proxy/...` at compile time.
  const promise = fetchImageBytes(src).then((bytes) => {
    pendingImages.delete(src);
    if (bytes) {
      blobCache.set(src, bytes);
      evictBlobsIfNeeded();
      const img = ck.MakeImageFromEncoded(bytes);
      if (img) {
        imageCache.set(src, img);
        evictIfNeeded();
        return img;
      }
    }
    // No bytes (network/throttle failure) or undecodable bytes (CanvasKit can't
    // rasterize SVG and a few other vector formats): fall through to the
    // <img>+canvas path, which uses the browser's native decoder. Without this,
    // brand logos in canvas IMAGE nodes render as empty boxes.
    return loadImageViaElement(ck, src).then((elImg) => {
      if (!elImg) failedImages.set(src, Date.now());
      return elImg;
    });
  });

  pendingImages.set(src, promise);
  return promise;
}

function loadImageViaElement(ck: CanvasKit, src: string): Promise<SkImage | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Cap the decode size: a pathological upload (16k x 16k) would
        // otherwise allocate a ~1GB RGBA buffer below and OOM the tab. 4096
        // comfortably covers a 600px email column at any sane zoom/DPR.
        const MAX_DECODE_DIM = 4096;
        const scale = Math.min(
          1,
          MAX_DECODE_DIM / Math.max(1, img.naturalWidth, img.naturalHeight),
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const skImg = ck.MakeImage(
          {
            width: canvas.width,
            height: canvas.height,
            alphaType: ck.AlphaType.Unpremul,
            colorType: ck.ColorType.RGBA_8888,
            colorSpace: ck.ColorSpace.SRGB,
          },
          imageData.data,
          4 * canvas.width,
        );
        if (skImg) {
          imageCache.set(src, skImg);
          evictIfNeeded();
        }
        resolve(skImg);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export function getCachedImage(src: string, nodeSize?: number): SkImage | undefined {
  if (nodeSize) {
    const optimized = optimizedImageUrl(src, { w: optimalWidth(nodeSize) });
    const img = imageCache.get(optimized);
    if (img) return img;
  }
  // Fall back to the same fuzzy/pathname match the renderer uses. Without this,
  // a query-string change to a node's imageSrc (e.g. the `?t=1` transparency
  // flag appended after background removal) misses the exact-URL lookup even
  // though the image is decoded under its old key — which silently dead-ends
  // crop/move/resize on the node while it still renders fine.
  return imageCache.get(src) ?? findCachedFallback(src) ?? undefined;
}

// ─── Render context ─────────────────────────────────────────────────────

export interface RenderContext {
  ck: CanvasKit;
  canvas: Canvas;
  doc: SceneDocument;
  fontMgr: FontMgr;
  onImageLoaded: () => void;
  editingTextId?: string | null;
  zoom?: number;
}

// ─── Node renderer (delegates to shared) ────────────────────────────────

export function renderNode(ctx: RenderContext, node: SceneNode): void {
  const sharedCtx: SceneRenderContext = {
    ck: ctx.ck,
    canvas: ctx.canvas,
    doc: ctx.doc,
    fontMgr: ctx.fontMgr,
    editingTextId: ctx.editingTextId,
    resolveImage: (src, nodeW, nodeH) => {
      const displaySize = Math.max(nodeW ?? 0, nodeH ?? 0) * (ctx.zoom || 1);
      const targetW = optimalWidth(displaySize);
      const optimizedSrc = optimizedImageUrl(src, { w: targetW });
      const exact = imageCache.get(optimizedSrc);
      if (exact) return exact;
      const fallback = findCachedFallback(src);
      if (fallback) {
        // Keep drawing the lower-res copy, but upgrade in the background to
        // the width this node actually needs — otherwise the first (often
        // 1200px) load is fuzzy-matched forever and a large node renders
        // soft. loadImage dedupes via its cache + pending maps, so calling
        // every frame is cheap.
        if (fallback.width() < Math.min(targetW, 2048)) {
          loadImage(ctx.ck, optimizedSrc).then((img) => {
            if (img) ctx.onImageLoaded();
          });
        }
        return fallback;
      }
      return null;
    },
    onImageMissing: (src) => {
      const optimizedSrc = optimizedImageUrl(src, { w: optimalWidth(600) });
      loadImage(ctx.ck, optimizedSrc).then((img) => {
        if (img) ctx.onImageLoaded();
      });
    },
  };
  sharedRenderNode(sharedCtx, node);
}
